import {
  IExperienceTemplateResource,
  ISubTaskWatchTemplate,
  StorageContext
} from '@dadohr/shared/domainTypes/experience';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import { TextField } from '@material-ui/core';
import React, { SetStateAction } from 'react';
import { DataFieldWithLabel } from '../../../../../../../../components/DataFieldWithLabel';
import {
  FormRow,
  FormRowGroup
} from '../../../../../../../../components/Form/FormRow';
import { TokenDefinition } from '../../../../../../../../components/MarkdownText';
import { useUnsafeTranslation } from '../../../../../../../../i18next';
import { BlockSettingsSubPage, TaskBuilderContext } from '../../../types';
import {
  TaskBuilderBlockValidationState,
  TaskBuilderBlockValidationStateItem,
  useFieldValidator,
  VALIDATORS_WATCH
} from '../../../validation';
import { OnCompleteHooks, OnCompleteHooksReadOnly } from './onCompleteHooks';

export const BlockSettingsSubTaskWatch = ({
  value,
  onChange,
  validation,
  setValidation,
  expTpl,
  customFields,
  resources,
  onChangeResources,
  goToSubPage,
  showOnCompleteHookSettings,
  tokens
}: {
  value: ISubTaskWatchTemplate;
  onChange: (nextValue: ISubTaskWatchTemplate) => void;
  validation: TaskBuilderBlockValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<TaskBuilderBlockValidationState>
  >;
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  resources: IExperienceTemplateResource[];
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  goToSubPage: (nextSubPage: BlockSettingsSubPage) => void;
  showOnCompleteHookSettings: boolean;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();
  const validateUrl = useFieldValidator(setValidation, VALIDATORS_WATCH, 'url');
  const validateHooks = useFieldValidator(
    setValidation,
    VALIDATORS_WATCH,
    'hooks'
  );
  const invalidUrlReason = validation?.fields.url || null;
  return (
    <>
      <FormRowGroup>
        <FormRow>
          <TextField
            label={t('Title')}
            value={value.data.label}
            fullWidth
            variant="outlined"
            onChange={(ev) => {
              const label = ev.target.value;
              onChange({
                ...value,
                data: {
                  ...value.data,
                  label
                }
              });
            }}
          />
        </FormRow>
        <FormRow>
          <TextField
            autoFocus
            label={t('URL')}
            value={value.data.url}
            fullWidth
            variant="outlined"
            error={!!invalidUrlReason}
            helperText={invalidUrlReason}
            onBlur={() => validateUrl(value, expTpl)}
            onChange={(ev) => {
              const nextValue = {
                ...value,
                data: {
                  ...value.data,
                  url: ev.target.value
                }
              };
              invalidUrlReason && validateUrl(nextValue, expTpl);
              onChange(nextValue);
            }}
          />
        </FormRow>
      </FormRowGroup>
      <OnCompleteHooks
        value={value.hooks.onComplete}
        onChange={(nextHooks) => {
          const nextValue = {
            ...value,
            hooks: {
              ...value.hooks,
              onComplete: nextHooks
            }
          };
          validateHooks(nextValue, expTpl);
          onChange(nextValue);
        }}
        tokens={tokens}
        resources={resources}
        onChangeResources={onChangeResources}
        expTpl={expTpl}
        customFields={customFields}
        goToSubPage={goToSubPage}
        validation={validation}
        showOnCompleteHookSettings={showOnCompleteHookSettings}
        storageContext={StorageContext.SUBTASK}
      />
    </>
  );
};

export const BlockSettingsSubTaskWatchReadOnly = ({
  value,
  resources,
  expTpl,
  customFields,
  showOnCompleteHookSettings,
  tokens
}: {
  value: ISubTaskWatchTemplate;
  resources: IExperienceTemplateResource[];
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  showOnCompleteHookSettings: boolean;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <>
      <FormRowGroup>
        <FormRow>
          <DataFieldWithLabel label={t('Video URL')}>
            {value.data.url || '-'}
          </DataFieldWithLabel>
        </FormRow>
        <FormRow>
          <DataFieldWithLabel label={t('Video Title')}>
            {value.data.label || '-'}
          </DataFieldWithLabel>
        </FormRow>
      </FormRowGroup>
      {showOnCompleteHookSettings && (
        <OnCompleteHooksReadOnly
          value={value.hooks.onComplete}
          resources={resources}
          expTpl={expTpl}
          customFields={customFields}
          tokens={tokens}
          storageContext={StorageContext.SUBTASK}
        />
      )}
    </>
  );
};
